import { AUTH_CONNECTION, WEB3AUTH_NETWORK } from '@web3auth/modal'
import { isAxiosError } from 'axios'
import { isLocalUrl, isPermittedUrl } from 'lib/routeUtils'
import { LoginProvider } from 'lib/web3auth'

export const getRedirecURL = () => {
  const currentURL = new URL(window.location.href)
  const redirectTo = currentURL.searchParams.get('redirect_to')
  if (redirectTo && (isLocalUrl(redirectTo) || isPermittedUrl(redirectTo))) {
    return redirectTo
  }
  return window.location.origin
}

export const getErrorMessage = (e: unknown) => {
  if (isAxiosError(e) && e.response?.data.error.code === 'not_found') {
    return 'Failed to login, do you need to create an account?'
  } else if (isAxiosError(e) && e.response?.data?.error?.message) {
    return e.response?.data.error.message
  } else if (isWeb3AuthError(e)) {
    return getWeb3AuthErrorMessage(e)
  } else {
    return 'Something went wrong when trying to login'
  }
}

export const getWeb3AuthErrorMessage = (e: unknown) => {
  const defaultMessage = 'Something went wrong when trying to login'

  const code = isWeb3AuthError(e) ? e.code : undefined

  switch (code) {
    case Web3AuthLoginErrorCode.POPUP_CLOSED:
      return 'User closed the login popup'
    default:
      return defaultMessage
  }
}

export enum Web3AuthLoginErrorCode {
  CUSTOM = 5000,
  INVALID_LOGIN_PARAMS = 5111,
  USER_NOT_LOGGED_IN = 5112,
  POPUP_CLOSED = 5113,
  LOGIN_FAILED = 5114,
  POPUP_BLOCKED = 5115,
  MFA_ALREADY_ENABLED = 5116
}

export const isWeb3AuthError = (e: unknown): e is Error & { code: Web3AuthLoginErrorCode } => {
  return e instanceof Error && 'code' in e && typeof e.code === 'number'
}

export const getAuthConnection = (loginProvider: LoginProvider) => {
  const connectionProvider = connectionProviders[loginProvider]
  if (!connectionProvider) {
    throw new Error(`Unsupported login provider: ${loginProvider}`)
  }
  return connectionProvider
}

const connectionProviders: Record<LoginProvider, (typeof AUTH_CONNECTION)[keyof typeof AUTH_CONNECTION]> = {
  apple: AUTH_CONNECTION.APPLE,
  discord: AUTH_CONNECTION.DISCORD,
  facebook: AUTH_CONNECTION.FACEBOOK,
  github: AUTH_CONNECTION.GITHUB,
  google: AUTH_CONNECTION.GOOGLE,
  kakao: AUTH_CONNECTION.KAKAO,
  line: AUTH_CONNECTION.LINE,
  linkedin: AUTH_CONNECTION.LINKEDIN,
  reddit: AUTH_CONNECTION.REDDIT,
  twitch: AUTH_CONNECTION.TWITCH,
  twitter: AUTH_CONNECTION.TWITTER,
  wechat: AUTH_CONNECTION.WECHAT
}

export const isValidWeb3AuthNetwork = (
  network: string
): network is (typeof WEB3AUTH_NETWORK)[keyof typeof WEB3AUTH_NETWORK] => {
  return Object.values(WEB3AUTH_NETWORK).includes(network as (typeof WEB3AUTH_NETWORK)[keyof typeof WEB3AUTH_NETWORK])
}
